window.dataLayer = window.dataLayer || [];

/**
 * Calculates the index of the closest parent `.shopify-section` element to the given element
 * within all `.shopify-section` elements inside `.main-content`.
 *
 * @param {Element} element - The DOM element to find the section index for.
 * @returns {number} The zero-based index of the section, or -1 if not found.
 */
function calculateSectionIndex(element) {
  const sections = Array.from(
    document.querySelectorAll(".main-content .shopify-section")
  );
  const section = element.closest(".shopify-section");

  return section ? sections.indexOf(section) : -1;
}

/**
 * Determines the type of page based on the given URL.
 *
 * @param {string} url - The URL to analyze.
 * @returns {string} The page type, which can be one of: "home", "collection", "product", "page", "blog", "cart", or "unknown".
 */
function getPageType(url) {
  if (!url) return "unknown";

  const cleanUrl = stripLocaleFromUrl(url);
  const path = cleanUrl.split(/[?#]/)[0];

  if (path === "/" || path === "") {
    return "home";
  }
  if (path.startsWith("/collections")) {
    return "collection";
  }
  if (path.startsWith("/products")) {
    return "product";
  }
  if (path.startsWith("/pages")) {
    return "page";
  }
  if (path.startsWith("/blogs")) {
    return "blog";
  }
  if (path.startsWith("/cart")) {
    return "cart";
  }

  return "unknown";
}

/**
 * Utility to send events to the dataLayer
 *
 * @param {string} eventName - GA4 event name
 * @param {object} params - Extra parameters
 */
function pushToDataLayer(eventName, params = {}) {
  window.dataLayer.push({
    event: "ga4_event",
    event_name: eventName,
    ...params,
  });
}

/**
 * Removes the locale segment from the beginning of a URL path.
 * The locale segment can be in the format `/xx/` or `/xx-yy/`, where `xx` is a two-letter language code
 * and `yy` is a two-letter country code.
 *
 * @param {string} url - The URL path to process (e.g., "/en-us/products").
 * @returns {string} The URL path with the locale segment removed (e.g., "/products").
 */
function stripLocaleFromUrl(url) {
  if (!url || typeof url !== "string") return url;

  if (url.startsWith("#")) {
    return url.slice(1);
  }

  // Regex to match locale patterns like /en/ or /en-us/ at the start of the URL
  const regex = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i;

  const result = url ? url.replace(regex, "") : url;
  return result === "" ? "/" : result;
}

/* ==========================
   AVAILABLE EVENTS
========================== */

/**
 * Click CTA
 *
 * @param {string} clickLabel - Button text or label
 * @param {string} clickUrl - Destination URL
 * @param {string} clickPosition - CTA position (only on Home Page)
 * @param {string} pageType - Page type (e.g. "home", "collection", "product")
 */
function gtmClickCta(
  clickLabel,
  clickUrl,
  clickPosition = null,
  pageType = null
) {
  const params = {
    click_id: stripLocaleFromUrl(clickUrl),
    click_label: clickLabel,
    click_url: clickUrl,
  };
  if (clickPosition) params.click_position = clickPosition;
  if (pageType) params.page_type = pageType;

  pushToDataLayer("click_cta", params);
}

/**
 * Click Menu
 *
 * @param {string} clickLabel - Menu item text
 * @param {string} clickUrl - Menu item URL
 * @param {string} clickElement - Clicked HTML element (optional)
 */
function gtmClickMenu(clickLabel, clickUrl, clickElement = null) {
  const params = {
    click_id: stripLocaleFromUrl(clickUrl),
    click_label: clickLabel,
    click_url: clickUrl,
  };
  if (clickElement) params.click_element = clickElement;

  pushToDataLayer("click_menu", params);
}

/**
 * Filter applied
 *
 * @param {string} category - Filter category (e.g. "color")
 * @param {string} value - Selected value (e.g. "red")
 */
function gtmFilter(category, value) {
  pushToDataLayer("filter", {
    filter_category: category,
    filter_value: value,
  });
}

/**
 * Newsletter subscription
 */
function gtmNewsletterSubscription() {
  pushToDataLayer("newsletter_subscription");
}

/* ==========================
   EVENT LISTENERS
========================== */

document.addEventListener("DOMContentLoaded", () => {
  [...document.querySelectorAll("main a, main button")]
    .filter((el) => {
      if (el.closest("[data-js-product-item]")) return false;
      if (el.hasAttribute("data-js-product-add-to-cart")) return false;
      if (el.getAttribute("title") === "Remove" && el.hasAttribute("hreflang")) return false;

      return true;
    })
    .forEach((element) => {
      element.addEventListener("click", () => {
        /**
         * Retrieves the label of a clickable element, prioritizing its inner text,
         * and falling back to its "aria-label" attribute if inner text is not available.
         *
         * @type {string}
         */
        const clickLabel = element.innerText || element.getAttribute("aria-label");
        const clickUrl = element.getAttribute("href") || null;
        const index = calculateSectionIndex(element);
        const url = window.location.pathname;

        gtmClickCta(clickLabel, clickUrl, `row${index + 1}`, getPageType(url));
      });
    });
});

/* ==========================
   GLOBAL EXPORT
========================== */

window.GTMEvents = {
  clickCta: gtmClickCta,
  clickMenu: gtmClickMenu,
  filter: gtmFilter,
  newsletter: gtmNewsletterSubscription,
};
